import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { SAN_ANTONIO_COORDS } from 'app/constants/map.constants';
import { ReportDetailsService } from 'app/services/report-details.service';
import { HeaderService } from 'app/services/header.service';
import { environment } from 'environments/environment';
import { GeoJSONSource, Map } from 'mapbox-gl';
import { FeatureCollection, Geometry } from 'geojson';
import { Subject, Subscription, throttleTime } from 'rxjs';
import { IOperationsDetails, TitledCount } from 'app/models/report-details.models';
import { ICitationCoordinatesWithId } from 'app/models/geo-spacial.models';
import { DeviceMaxSize } from 'app/constants/common.constants';
import { NgFor, NgIf } from '@angular/common';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';
import { UserDetailsComponent } from '../user-details/user-details.component';
import { MatFormField } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { MatOption } from '@angular/material/autocomplete';
import { CitationDetailsComponent } from '../citation-details/citation-details.component';

@Component({
  selector: 'app-report-details',
  templateUrl: './report-details.component.html',
  styleUrls: ['./report-details.component.scss'],
  imports: [
    NgIf,
    MatProgressSpinner,
    RouterLink,
    MatIcon,
    UserDetailsComponent,
    MatFormField,
    MatSelect,
    FormsModule,
    NgFor,
    MatOption,
    CitationDetailsComponent,
  ],
})
export class ReportDetailsComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild('mapContainer') mapContainer!: ElementRef;
  @ViewChild('content') content!: ElementRef;

  userDetails: IOperationsDetails | null = null;
  top5Citations: TitledCount[] = [];
  genders: TitledCount[] = [];
  races: TitledCount[] = [];
  citationCoordinates: ICitationCoordinatesWithId[] = [];

  private map?: Map;
  private containerSizeChanged: Subject<void> = new Subject<void>();
  private observer = new ResizeObserver(() => {
    if (!this.map) return;
    this.containerSizeChanged.next();
  });
  private containerSizeChangedSub?: Subscription;
  private exportSubscripton!: Subscription;

  loading = true;

  numberOfDaysOptions = [
    { value: '7', viewValue: 'Last 7 days' },
    { value: '30', viewValue: 'Last 30 days' },
    { value: '60', viewValue: 'Last 60 days' },
    { value: '90', viewValue: 'Last 90 days' },
  ];
  numberOfDays = '7';

  constructor(
    private route: ActivatedRoute,
    private reportDetailsService: ReportDetailsService,
    private headerService: HeaderService,
  ) {}

  ngOnInit(): void {
    this.exportSubscripton = this.headerService.exportChange.subscribe(() => {
      this.exportToPdf();
    });
  }

  private initializeMap() {
    this.map = new Map({
      accessToken: environment.mapKey,
      container: this.mapContainer.nativeElement,
      style: 'mapbox://styles/ivankulik/clh7i8vxo00tj01qu3798hfi7', // style URL
      center: SAN_ANTONIO_COORDS, // TODO: use bounds based on last 25 citations instead?
      zoom: 10,
      preserveDrawingBuffer: true,
    });

    /* eslint-disable @typescript-eslint/no-unused-vars */
    this.map.on('load', (e) => {
      this.updateCitationsMarkers();
    });
  }

  private cloneMapCanvas(): HTMLCanvasElement | undefined {
    if (!this.map) return;

    const canvas = this.map.getCanvas();
    const newMapCanvas = document.createElement('canvas');
    const context = newMapCanvas.getContext('2d');

    newMapCanvas.width = canvas.width;
    newMapCanvas.height = canvas.height;

    if (context) {
      context.drawImage(canvas, 0, 0);
    }

    return newMapCanvas;
  }

  private getCanvasProperties(
    canvas: HTMLCanvasElement,
    doc: jsPDF,
  ): {
    img: string;
    height: number;
  } {
    const img = canvas.toDataURL('image/png');
    const pageWidth = doc.internal.pageSize.getWidth();
    const height = canvas.height * (pageWidth / canvas.width);

    return {
      img,
      height,
    };
  }

  private mapToPdf(doc: jsPDF, imgHeight: number) {
    if (!this.map) return;

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const pagesCount = Math.ceil(imgHeight / pageHeight);

    const { img: mapImg, height: mapImgHeight } = this.getCanvasProperties(this.map.getCanvas(), doc);

    const padding = 10;
    const heightLeft = imgHeight - (pagesCount - 1) * pageHeight;
    const isMapOverflow = heightLeft + mapImgHeight + padding > pageHeight;

    if (isMapOverflow) doc.addPage();

    const mapPosition = isMapOverflow ? padding : imgHeight + padding;
    doc.addImage(mapImg, 'jpg', 0, mapPosition, pageWidth, mapImgHeight);
  }

  private canvasToPdf(canvas: HTMLCanvasElement) {
    const isMobile = window.innerHeight <= DeviceMaxSize.MOBILE;

    const doc = new jsPDF({
      orientation: isMobile ? 'p' : 'landscape',
      unit: 'mm',
      format: 'a4',
      putOnlyUsedFonts: true,
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const { img, height: imgHeight } = this.getCanvasProperties(canvas, doc);

    let heightLeft = imgHeight;
    let position = 10;

    doc.addImage(img, 'jpg', 0, position, pageWidth, imgHeight);

    heightLeft -= pageHeight;
    while (heightLeft >= 0) {
      position = heightLeft - imgHeight;
      doc.addPage();
      doc.addImage(img, 'jpg', 0, position, pageWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    this.mapToPdf(doc, imgHeight);

    const name = [this.userDetails?.officerName].filter(Boolean).join('-');
    doc.save(`report-details-${name}`);
  }

  private async exportToPdf(): Promise<void> {
    const content = this.content.nativeElement.cloneNode(true) as HTMLDivElement;
    content.setAttribute('type', 'hidden');
    content.style.minHeight = '0';

    const arrowElem = content.querySelector('div.mat-mdc-select-arrow-wrapper');
    const mapElem = content.querySelector('#mapContainer');

    arrowElem && arrowElem.remove();
    mapElem && content.removeChild(mapElem);
    document.body.appendChild(content);

    const canvas = await html2canvas(content, {
      imageTimeout: 2000,
      removeContainer: true,
      scrollY: -window.scrollY,
    });

    document.body.removeChild(content);

    this.canvasToPdf(canvas);
  }

  ngAfterViewInit() {
    this.updateData();

    // put it in request subscription
    this.observer.observe(this.mapContainer.nativeElement);
    this.containerSizeChangedSub = this.containerSizeChanged
      .pipe(throttleTime(200))
      .subscribe(() => this.map?.resize());
  }

  updateData() {
    const id = this.route.snapshot.params['id'];
    this.reportDetailsService.getReportDetails(id, Number(this.numberOfDays)).subscribe((result) => {
      this.loading = false;
      if (result) {
        this.userDetails = result;
        this.top5Citations = result.topNames;
        this.genders = result.topGenders;
        this.races = result.topRaces;
        this.citationCoordinates = result.lastLocations;
      }
      if (this.map?.loaded()) {
        this.updateCitationsMarkers();
      } else {
        this.initializeMap();
      }
    });
  }

  onNumberOfDaysChanged() {
    this.updateData();
  }

  ngOnDestroy(): void {
    this.containerSizeChangedSub?.unsubscribe();
    this.exportSubscripton.unsubscribe();
    this.observer?.disconnect();
  }

  get combineCitationDetails() {
    return [
      { title: 'Top 5 Citations Written', data: this.top5Citations },
      { title: 'Gender', data: this.genders },
      { title: 'Race', data: this.races },
    ];
  }

  private getCitationsFeatureCollection(): FeatureCollection<Geometry> {
    const data: FeatureCollection<Geometry> = {
      type: 'FeatureCollection',
      features: [],
    };

    for (const citation of this.citationCoordinates) {
      data.features.push({
        type: 'Feature',
        properties: {
          id: citation.id,
        },
        geometry: {
          type: 'Point',
          coordinates: [citation.longitude, citation.latitude],
        },
      });
    }
    return data;
  }

  private updateCitationsMarkers() {
    if (!this.map) return;

    const sourceId = 'citation-markers';
    const data = this.getCitationsFeatureCollection();

    if (this.map.getSource(sourceId)) {
      (this.map.getSource(sourceId) as GeoJSONSource).setData(data);
    } else {
      this.map.addSource(sourceId, {
        type: 'geojson',
        data: data,
        cluster: true,
        clusterMaxZoom: 22, // Max zoom to cluster points on
      });
      this.map.addLayer({
        id: 'citation-markers-cluster-count',
        type: 'symbol',
        source: sourceId,
        filter: ['has', 'point_count'],
        layout: {
          'text-field': ['get', 'point_count_abbreviated'],
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12,
        },
      });
      this.map.addLayer(
        {
          id: 'citation-markers-clusters',
          type: 'circle',
          source: sourceId,
          filter: ['has', 'point_count'],
          paint: {
            'circle-color': '#51bbd6',
            'circle-radius': 20,
          },
        },
        'citation-markers-cluster-count',
      );

      const unclusteredId = 'citation-markers-unclustered-point';
      this.map.addLayer({
        id: unclusteredId,
        type: 'symbol',
        source: sourceId,
        filter: ['!', ['has', 'point_count']],
        layout: {
          'icon-image': 'general-citation-marker',
        },
      });
    }
  }
}
